import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { useWorkspaces } from "@/contexts/WorkspaceContext";
import { toast } from "sonner";

export default function CreateWorkspaceDialog({ open, onOpenChange }) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { refetch } = useWorkspaces();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setName("");
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/workspaces", { name: trimmed });
      await refetch();
      onOpenChange(false);
      navigate(`/w/${data.slug}`);
      toast.success(`Workspace "${data.name}" created`);
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-workspace-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">
            Create a workspace
          </DialogTitle>
          <DialogDescription className="font-sans text-sm text-[#7A7571]">
            A workspace is a private space for a group of humans to share chats
            with Claude. You'll be the owner.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="px-1 pt-2 pb-1 space-y-2">
          <label
            className="block font-sans text-xs uppercase tracking-widest text-[#7A7571] font-semibold"
            htmlFor="create-workspace-name"
          >
            Workspace name
          </label>
          <input
            id="create-workspace-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="create-workspace-name-input"
            placeholder="e.g. Acme Co."
            className="w-full px-4 py-3 rounded-lg bg-white border border-[#DCD7CE] font-sans text-base text-[#2C2A29] placeholder:text-[#A5A29E] focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30 focus:border-[#D96C4A]/40 transition"
          />
          <p className="font-sans text-xs text-[#A5A29E]">
            The slug will be derived from the name and stay the same even if
            you rename it later.
          </p>
        </form>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="create-workspace-cancel"
            className="border-[#DCD7CE]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
            data-testid="create-workspace-submit"
            className="bg-[#2C2A29] hover:bg-[#1A1918] text-white"
          >
            {submitting ? "Creating…" : "Create workspace"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
